import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { createLogger } from '../../../shared/config/logger';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';
import { redactSensitive } from '../../../shared/utils/logSanitization';
const logger = createLogger('Reset-password.jsx');

export default function ResetPassword() {
    const [formData, setFormData] = useState({ email: '' });
    const [state, setState] = useState({ error: null, loading: false, message: null });
    const { resetPassword } = useAuth();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateForm = (formData) => {
        const { email } = formData;
        if (!email) {
            return 'Email is required';
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return 'Invalid email format';
        }
        // No errors found
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setState((prevState) => ({ ...prevState, error: null, loading: true, message: null }));

        const errorMessage = validateForm(formData);
        if (errorMessage) {
            setState((prevState) => ({ ...prevState, error: errorMessage, loading: false }));
            return;
        }

        setState((prevState) => ({
            ...prevState,
            error: null,
            message: 'Password reset initiated...',
        }));

        logger.info('Password reset form submitted', redactSensitive(formData));
        try {
            await resetPassword(formData);
            setState((prevState) => ({
                ...prevState,
                error: null,
                loading: false,
                message: 'If your email is registered with us, check your inbox',
            }));
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            logger.error('Error in password reset form submission', { error: errorMessage });
            setState((prevState) => ({
                ...prevState,
                error: errorMessage,
                loading: false,
                message: null,
            }));
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
            <form
                onSubmit={handleSubmit}
                className="w-full max-w-md bg-white p-8 rounded-2xl shadow-lg space-y-6"
            >
                <h2 className="text-2xl font-semibold text-center text-gray-800">
                    Enter your email to reset your password.
                </h2>

                {state.error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {state.error}
                    </div>
                )}
                {state.message && !state.error && (
                    <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
                        {state.message}
                    </div>
                )}
                <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email"
                    disabled={state.loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498db] transition"
                    required
                />
                <button
                    type="submit"
                    className="w-full bg-card text-white py-3 rounded-lg hover:bg-primary transition font-medium"
                    disabled={state.loading}
                >
                    {state.loading ? 'Sending reset email...' : 'Send password reset email'}
                </button>
            </form>
        </div>
    );
}
